import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export default function ResetPasswordPage() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading'); // loading | ready | invalid | done
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Supabase processes the URL hash (#access_token=...&type=recovery) automatically.
    // Listen for the PASSWORD_RECOVERY event to know when the session is ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setStatus('ready');
      } else if (event === 'SIGNED_IN' && session) {
        // Fallback: also works if the session was already established before mount
        setStatus('ready');
      }
    });

    // Check if there's already an active recovery session (page refresh case)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setStatus('ready');
      } else {
        // Give the onAuthStateChange a moment to fire before declaring invalid
        setTimeout(() => {
          setStatus((prev) => prev === 'loading' ? 'invalid' : prev);
        }, 3000);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await updatePassword(password);
      setStatus('done');
      toast.success('Password updated successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(err.message || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-3">
            <img src="/logo.png" alt="ReadTheRoom" className="h-14 w-14 rounded-xl object-cover mx-auto" />
          </Link>
        </div>

        {status === 'loading' && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-terra mx-auto mb-3" />
            <p className="text-ink-muted text-sm">Verifying your reset link...</p>
          </div>
        )}

        {status === 'invalid' && (
          <div className="bg-red-50 border border-red-200 rounded-2xl px-8 py-10 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-ink mb-3">Link expired</h2>
            <p className="text-ink-light text-sm leading-relaxed mb-6">
              This password reset link has expired or is invalid.
            </p>
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-terra hover:text-terra/80"
            >
              Request a new reset link
            </Link>
          </div>
        )}

        {status === 'done' && (
          <div className="bg-forest/10 border border-forest/20 rounded-2xl px-8 py-10 text-center">
            <CheckCircle className="w-12 h-12 text-forest mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-ink mb-3">Password updated!</h2>
            <p className="text-ink-muted text-sm">Redirecting you to sign in...</p>
          </div>
        )}

        {status === 'ready' && (
          <Card className="bg-cream border-cream-dark shadow-sm">
            <CardHeader className="pb-0 pt-6 px-6">
              <h1 className="font-heading text-2xl text-ink">Choose New Password</h1>
              <p className="text-ink-muted text-sm mt-1">Must be at least 8 characters.</p>
            </CardHeader>
            <CardContent className="px-6 pb-6 pt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-ink-light text-sm font-medium">
                    New Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm" className="text-ink-light text-sm font-medium">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirm"
                    type="password"
                    placeholder="Repeat your password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className={`bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted ${
                      confirm && confirm !== password ? 'border-red-400' : ''
                    }`}
                    required
                  />
                  {confirm && confirm !== password && (
                    <p className="text-xs text-red-500">Passwords do not match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !password || !confirm || password !== confirm}
                  className="w-full bg-terra hover:bg-terra/90 text-cream font-semibold h-10 mt-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
